import { createSlice } from '@reduxjs/toolkit'
import { isValidFilterConfig } from '../../../utilities/commonUtilities';
import i18n from '../../../utilities/i18n';

let initialState = {
    tableData: [],
    loading: false,
    pageSize: 0,
    count: 0,
    totalPage: 0,
    sortField: "", // Current SortField
    orderType: "", // Current Sort Order Asc or Dsc
    filterConfig: [
        {
            fieldName: 'dateOfServiceFrom',
            label: i18n.t("reports.filters.dateOfServiceFrom"),
            type: 'dateField',
        },
        {
            fieldName: 'dateOfServiceTo',
            label: i18n.t("reports.filters.dateOfServiceTo"),
            type: 'dateField',
            minDateField: "dateOfServiceFrom"
        },
        {
            fieldName: 'dateOfEntryFrom',
            label: i18n.t("reports.filters.dateOfEntryFrom"),
            type: 'dateField'
        },
        {
            fieldName: 'dateOfEntryTo',
            label: i18n.t("reports.filters.dateOfEntryTo"),
            type: 'dateField',
            minDateField: "dateOfEntryFrom"
        },
        {
            fieldName: 'firstBilledDateFrom',
            label: i18n.t("reports.filters.firstBilledDateFrom"),
            type: 'dateField'
        },
        {
            fieldName: 'firstBilledDateTo',
            label: i18n.t("reports.filters.firstBilledDateTo"),
            type: 'dateField',
            minDateField: "firstBilledDateFrom"
        },
        {
            fieldName: 'lastBilledDateFrom',
            label: i18n.t("reports.filters.lastBilledDateFrom"),
            type: 'dateField'
        },
        {
            fieldName: 'lastBilledDateTo',
            label: i18n.t("reports.filters.lastBilledDateTo"),
            type: 'dateField',
            minDateField: "lastBilledDateFrom"
        },
        {
            fieldName: 'payerType',
            label: i18n.t("reports.filters.payerType"),
            type: 'multiSelect',
            dropDownOptionName: 'payerTypes'
        },
        {
            fieldName: 'insurance_companies',
            label: i18n.t("reports.filters.insuranceCompanies"),
            type: 'multiSelect',
            dropDownOptionName: 'insuranceCompanyList'
        },
        {
            fieldName: 'claimStatus',
            label: i18n.t("reports.filters.claimStatus"),
            type: 'multiSelect',
            dropDownOptionName: 'claimStatusList'
        },
        {
            fieldName: 'claimSubStatus',
            label: i18n.t("reports.filters.claimSubStatus"),
            type: 'multiSelect',
            dropDownOptionName: 'claimSubStatusList'
        },
        {
            fieldName: 'renderingProvider',
            label: i18n.t("reports.filters.renderingProvider"),
            type: 'multiSelect',
            dropDownOptionName: 'renderingProviderList'
        },
        {
            fieldName: 'refferingProvider',
            label: i18n.t("reports.filters.refferingProvider"),
            type: 'multiSelect',
            dropDownOptionName: 'referringProviderList'
        },
        {
            fieldName: 'billingProvider',
            label: i18n.t("reports.filters.billingProvider"),
            type: 'multiSelect',
            dropDownOptionName: 'renderingProviderList'
        },
        {
            fieldName: 'serviceLocation',
            label: i18n.t("reports.filters.serviceLocation"),
            type: 'multiSelect',
            dropDownOptionName: 'serviceLocationList'
        },
        {
            fieldName: 'cptList',
            label: i18n.t("reports.filters.cptList"),
            type: 'multiSelect',
            dropDownOptionName: 'cptList'
        },
        {
            fieldName: 'ICD',
            label: i18n.t("reports.filters.ICD"),
            type: 'multiSelect',
            dropDownOptionName: 'icdList'
        },
        {
            fieldName: 'aging',
            label: i18n.t("reports.filters.aging"),
            type: 'select',
            dropDownOptionName: 'agingList'
        },
        {
            fieldName: 'balanceAmount',
            label: i18n.t("reports.filters.balanceAmountTotals"),
            type: 'amountFilter',
            fieldPrefix: 'balanceAmountTotals',
        }
    ].map(item => {
        if (!isValidFilterConfig(item)) {
            throw new Error(`Invalid filter config for ${item.fieldName}`);
        }
        return item;
    }),
    filters: { // Filter input variables
        dateOfServiceFrom: "",
        dateOfServiceTo: "",
        dateOfEntryFrom: "",
        dateOfEntryTo: "",
        firstBilledDateFrom: "",
        firstBilledDateTo: "",
        lastBilledDateFrom: "",
        lastBilledDateTo: "",
        payerType: [],
        insurance_companies: [],
        claimStatus: [],
        claimSubStatus: [],
        renderingProvider: [],
        refferingProvider: [],
        billingProvider: [],
        serviceLocation: [],
        cptList: [],
        ICD: [],
        balanceAmountTotalsType: "",
        balanceAmountTotalsFrom: "",
        balanceAmountTotalsTo: "",
        aging: "",
    },
    allSortFieldStates: {
        "custom_claim_id": true,
        "first_billed_date": true,
        "last_billed_date": true,
        "service_from_date": true,
        "service_to_date": true,
        "current_payer_name": true,
        "age_from_dos": true,
        "age_from_date_of_entry": true,
        "age_from_first_billed_date": true,
        "age_from_last_billed_date": true
    },
    isError: 'not-set'
}

export const OpenArReportSlice = createSlice({
    name: "openArReport",
    initialState,
    reducers: {
        onResetData: () => initialState,
        updateOpenArReport: (state, action) => {
            if (action.payload.type === "success") {
                state.count = action.payload.data.data.count;
                state.pageSize = action.payload.data.data.page_size;
                state.totalPage = Math.ceil(action.payload.data.data.count / action.payload.data.data.page_size)
                state.tableData = action.payload.data.data.results;
            }
            if (action.payload.type === "error") {
                state.isError = true
            }
        },
        updateSortField: (state, actions) => {
            // toggle all the sortfields to true except for the currently sorting field
            const newSortState = Object.keys(state.allSortFieldStates).reduce((acc, key) => {
                acc[key] = key === actions.payload.sortField ? false : true;
                return acc;
            }, {});
            return {
                ...state,
                sortField: actions.payload.sortField,
                orderType: actions.payload.sortOrder,
                allSortFieldStates: newSortState
            };
        },

        updateFieldValues: (state, actions) => {
            const updateState = actions.payload.state;
            const value = actions.payload.value;
            const field = actions.payload.field;
            if (updateState !== 'mainState') {
                return {
                    ...state,
                    [updateState]: {
                        ...state[updateState],
                        [field]: value,
                    }
                }
            } else {
                return {
                    ...state,
                    [field]: value,
                }
            }
        },
    }
});

export const {
    onResetData,
    updateOpenArReport,
    updateSortField,
    updateFieldValue
} = OpenArReportSlice.actions

export default OpenArReportSlice.reducer;